import type { ProductCostsSource, SnapshotType } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";

// ─── Goal-scoped snapshot query keys (legacy — retained for backward compat) ─

export const publicGoalSnapshotQueryKeys = {
  portfolio: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "snapshot", "portfolio"] as const,
  xray: (goalId: string | null) => ["publicFlow", "goals", goalId, "snapshot", "xray"] as const,
  assetAllocation: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "snapshot", "assetAllocation"] as const,
  historicalReturn: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "snapshot", "historicalReturn"] as const,
  forecast: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "snapshot", "forecast"] as const,
  cost: (goalId: string | null) => ["publicFlow", "goals", goalId, "snapshot", "cost"] as const,
  expectedValue: (goalId: string | null) =>
    ["publicFlow", "goals", goalId, "snapshot", "expectedValue"] as const,
  /** Matches all snapshot queries for a goal — for bulk invalidation */
  allForGoal: (goalId: string) => ["publicFlow", "goals", goalId, "snapshot"] as const,
};

// ─── Portfolio-scoped snapshot query keys (QP-150) ──────────────────────────
// Mirrors the key shape used by usePortfolioSnapshot (use-portfolio-snapshot.ts)
// for the public flow: ["publicFlow", "portfolios", portfolioId, "snapshot", type].

export const publicPortfolioSnapshotQueryKeys = {
  portfolio: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "portfolio"] as const,
  xray: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "xray"] as const,
  assetAllocation: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "assetAllocation"] as const,
  historicalReturn: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "historicalReturn"] as const,
  forecast: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "forecast"] as const,
  cost: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "cost"] as const,
  expectedValue: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "expectedValue"] as const,
  sustainability: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "sustainability"] as const,
  /** QL-417 — nested under "snapshot" so allForPortfolio invalidates it too. */
  productCosts: (portfolioId: string | null, source: ProductCostsSource, language: string) =>
    [
      "publicFlow",
      "portfolios",
      portfolioId,
      "snapshot",
      "productCosts",
      source,
      language,
    ] as const,
  /** Every source and language of one portfolio's product costs. */
  productCostsAll: (portfolioId: string) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "productCosts"] as const,
  /** QL-416 — per-portfolio cost/benefit comparison. */
  costBenefit: (portfolioId: string | null) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot", "costBenefit"] as const,
  allForPortfolio: (portfolioId: string) =>
    ["publicFlow", "portfolios", portfolioId, "snapshot"] as const,
};

// ─── Invalidation helper ────────────────────────────────────────────────────

const SNAPSHOT_TYPE_TO_KEY: Record<
  SnapshotType,
  (portfolioId: string | null) => readonly unknown[]
> = {
  portfolio: publicPortfolioSnapshotQueryKeys.portfolio,
  // QL-707 — never emitted in refreshHints; entry exists only for Record exhaustiveness.
  advisedPortfolio: () => ["publicFlow", "snapshot", "advisedPortfolio"] as const,
  xray: publicPortfolioSnapshotQueryKeys.xray,
  assetAllocation: publicPortfolioSnapshotQueryKeys.assetAllocation,
  historicalReturn: publicPortfolioSnapshotQueryKeys.historicalReturn,
  forecast: publicPortfolioSnapshotQueryKeys.forecast,
  cost: publicPortfolioSnapshotQueryKeys.cost,
  expectedValue: publicPortfolioSnapshotQueryKeys.expectedValue,
  sustainability: publicPortfolioSnapshotQueryKeys.sustainability,
  costBenefit: publicPortfolioSnapshotQueryKeys.costBenefit,
  // Instance-global (QP-224); the server never emits it in refreshHints — the
  // entry exists only for Record exhaustiveness.
  clientPreferences: () => ["publicFlow", "snapshot", "clientPreferences"] as const,
  // QL-315 — neither portfolio-keyed nor instance-keyed from here: the rows are
  // goal-scoped and instance-scoped. Handled by the predicate below instead, so
  // this entry exists only for Record exhaustiveness.
  assetClassForecast: () => ["publicFlow", "snapshot", "assetClassForecast"] as const,
  // QL-375 — advisor-only; never read on the public flow. Entry for exhaustiveness.
  mandate: () => ["publicFlow", "snapshot", "mandate"] as const,
};

/**
 * QL-315 — invalidate both scopes of the asset-class forecast.
 *
 * The helper below is handed a portfolioId, and the QL-315 rows are keyed by
 * goal and by instance, so no key function derived from that id can reach them.
 * A public session is one instance and its own goals, so matching every
 * asset-class-forecast key in the `publicFlow` tree is exact enough.
 */
function invalidatePublicAssetClassForecast(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: ({ queryKey: k }) =>
      k[0] === "publicFlow" &&
      k[3] === "snapshot" &&
      (k[4] === "assetClassForecast" || k[4] === "instanceAssetClassForecast"),
  });
}

/**
 * QL-315 — invalidate both scopes of the asset-class forecast, for the public
 * callers that change the merge without a portfolio calculation and therefore
 * get no `refreshHints`: adding or deleting a goal, deleting a portfolio.
 */
export function useInvalidatePublicAssetClassForecast() {
  const queryClient = useQueryClient();
  return () => invalidatePublicAssetClassForecast(queryClient);
}

/**
 * QL-675 — invalidate every portfolio's snapshot views after a whole-instance
 * rebuild: the goal-keyed invalidations never reach these portfolio-keyed
 * queries. A public session is one instance, so matching every portfolio in
 * the `publicFlow` tree is exact.
 */
export function useInvalidateAllPublicPortfolioSnapshots() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      predicate: ({ queryKey: k }) =>
        k[0] === "publicFlow" && k[1] === "portfolios" && k[3] === "snapshot",
    });
}

export function useInvalidatePublicPortfolioSnapshots() {
  const queryClient = useQueryClient();
  return (
    portfolioId: string,
    refreshHints?: { invalidated?: SnapshotType[]; cleared?: boolean },
  ) => {
    if (!refreshHints) return;
    if (refreshHints.cleared) {
      queryClient.invalidateQueries({
        queryKey: publicPortfolioSnapshotQueryKeys.allForPortfolio(portfolioId),
      });
      invalidatePublicAssetClassForecast(queryClient);
    } else if (refreshHints.invalidated?.length) {
      // QL-417 — product costs read the live composition, not a stored
      // snapshot type, so a composition regen ("portfolio") refreshes them.
      if (refreshHints.invalidated.includes("portfolio")) {
        queryClient.invalidateQueries({
          queryKey: publicPortfolioSnapshotQueryKeys.productCostsAll(portfolioId),
        });
      }
      for (const type of refreshHints.invalidated) {
        const keyFn = SNAPSHOT_TYPE_TO_KEY[type];
        if (keyFn && type !== "assetClassForecast") {
          queryClient.invalidateQueries({ queryKey: keyFn(portfolioId) });
        }
        if (type === "assetClassForecast") {
          invalidatePublicAssetClassForecast(queryClient);
        }
      }
    }
  };
}
